import { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  ThemeProvider,
  LinearProgress,
  useMediaQuery
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import GroupIcon from '@mui/icons-material/Group';
import NavBar from '../../layout/NavBar';
import { useAuth } from '../../context/AuthContext';
import { GruppenApi } from '../../../services/api';
import { darkTheme } from '../../../theme/darkTheme';
import { useNavigate, Navigate } from 'react-router-dom';
import NavBarBot from '../../layout/NavBarBot';
import Notification from '../../util/notifications/Notification';
import BackButton from '../../util/buttons/BackButton';
import HeaderCard from '../../layout/HeaderCard';
import LoadingPage from '../../layout/LoadingPage';
import GruppenCard from './components/gruppenuebersicht/GruppenCard';
import GruppeErstellenDialog from './components/gruppenuebersicht/GruppeErstellenDialog';

function GruppenUebersicht() {
  const isMobile = useMediaQuery(darkTheme.breakpoints.down('md'));
  const { nutzer, isLoggedIn, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [, setShowNotification] = useState(false);
  const [gruppen, setGruppen] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ type: "", text: "" });

  const [dialogOpen, setDialogOpen] = useState(false);
  const [neuGruppe, setNeuGruppe] = useState({ name: '', beschreibung: '' });

  useEffect(() => {
    if (!isLoggedIn || !nutzer?.id) {
      setLoading(false);
      return;
    }
    fetchGruppen();
  }, [nutzer, isLoggedIn]);

  const fetchGruppen = async () => {
    try {
      const response = await GruppenApi.getGruppenByNutzer(nutzer.id);
      const validGruppen = Array.isArray(response.data) ? response.data.filter((gruppe) => gruppe && gruppe.id) : [];
      setGruppen(validGruppen);
    } catch (err) {
      console.error('Fehler beim Laden der Gruppen:', err);
      setMessage({ type: 'error', text: 'Gruppen konnten nicht geladen werden' });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateGruppe = async () => {
    if (!neuGruppe.name.trim()) {
      setMessage({ type: 'error', text: 'Bitte Gruppennamen eingeben' });
      return;
    }

    try {
      await GruppenApi.createGruppe({
        name: neuGruppe.name,
        beschreibung: neuGruppe.beschreibung,
        ersteller_id: nutzer.id,
      });
      setMessage({ type: 'success', text: 'Gruppe erfolgreich erstellt' });
      setDialogOpen(false);
      setNeuGruppe({ name: '', beschreibung: '' });
      fetchGruppen();
    } catch (err) {
      console.error('Fehler beim Erstellen der Gruppe:', err);
      setMessage({ type: 'error', text: 'Gruppe konnte nicht erstellt werden' });
    }
  };

  const handleSetFavorit = async (gruppeId) => {
    try {
      await GruppenApi.setFavorit({
        gruppeId,
        nutzerId: nutzer.id,
      });
      setMessage({ type: 'success', text: 'Favorit gesetzt' });
      fetchGruppen();
    } catch (err) {
      console.error('Fehler beim Setzen des Favoriten:', err);
      setMessage({ type: 'error', text: 'Favorit konnte nicht gesetzt werden' });
    }
  };

  const handleLeaveGruppe = async (gruppeId, gruppeName) => {
    if (!window.confirm(`Möchtest du die Gruppe "${gruppeName}" wirklich verlassen?`)) {
      return;
    }

    try {
      await GruppenApi.leaveGruppe({
        gruppeId,
        nutzerId: nutzer.id,
      });
      setMessage({ type: 'success', text: 'Gruppe verlassen' });
      fetchGruppen();
    } catch (err) {
      console.error('Fehler beim Verlassen der Gruppe:', err);
      setMessage({ type: 'error', text: 'Gruppe konnte nicht verlassen werden' });
    }
  };

  const handleDeleteGruppe = async (gruppeId, gruppeName) => {
    if (!window.confirm(`Möchtest du die Gruppe "${gruppeName}" wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.`)) {
      return;
    }

    try {
      await GruppenApi.deleteGruppe({
        gruppeId,
        erstellerId: nutzer.id,
      });
      setMessage({ type: 'success', text: 'Gruppe gelöscht' });
      fetchGruppen();
    } catch (err) {
      console.error('Fehler beim Löschen der Gruppe:', err);
      setMessage({ type: 'error', text: 'Gruppe konnte nicht gelöscht werden' });
    }
  };

  // Warten bis Auth geladen ist
  if (authLoading) {
    return (
      <LoadingPage />
    );
  }

  // Wenn nicht eingeloggt, zur Login-Seite navigieren
  if (!isLoggedIn) {
    return <Navigate to="/login" replace />;
  }

  return (
    <>
      <ThemeProvider theme={darkTheme}>
        <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', pb: "44px" }}>
          <NavBar />
          <Container maxWidth="lg" sx={{ pt: { xs: 2, md: 4 } }}>
            {!isMobile && (
              <BackButton />
            )}
            <HeaderCard title={"Meine Gruppen"} subtitle={"Verwalte deine Traiingsgruppen"} />
            {message.status === "error" || message.status === "success" && (
              <Notification
                type={message.type}
                message={message.text}
                onClose={() => {
                  setShowNotification(true);
                  setMessage({ type: "", text: "" });
                }}
              />
            )}

            {loading ? (
              <LinearProgress />
            ) : gruppen.length === 0 ? (
              <Card>
                <CardContent sx={{ textAlign: 'center', py: 6 }}>
                  <GroupIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    Noch keine Gruppen
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={3}>
                    Erstelle deine erste Gruppe oder warte auf eine Einladung
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setDialogOpen(true)}
                  >
                    Gruppe erstellen
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <Grid container spacing={3}>
                {gruppen.map((gruppe) =>
                  gruppe && gruppe.id ? (
                    <Grid key={gruppe.id}>
                      <GruppenCard
                        gruppe={gruppe}
                        nutzerId={nutzer.id}
                        onFavorit={handleSetFavorit}
                        onOpen={(id) => navigate(`/gruppen/${id}`)}
                        onDelete={handleDeleteGruppe}
                        onLeave={handleLeaveGruppe}
                      />
                    </Grid>
                  ) : null
                )}
              </Grid>
            )}
          </Container>

          <GruppeErstellenDialog
            open={dialogOpen}
            onClose={() => setDialogOpen(false)}
            neuGruppe={neuGruppe}
            onNeuGruppe={setNeuGruppe}
            onCreate={handleCreateGruppe}
          />
        </Box>
      </ThemeProvider>
      <NavBarBot mainBtnF={setDialogOpen} mainBtnTxt={<AddIcon />} />
    </>
  );
}

export default GruppenUebersicht;
